import React, { useState } from 'react';
import { Form, Button, Row, Col, InputGroup, Badge, Spinner } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { collection, addDoc, serverTimestamp, getFirestore } from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { FaPlus, FaTimes } from 'react-icons/fa';

const ProfesionForm = ({ profesion, onSave, onCancel, usuario }) => {
    const [submitting, setSubmitting] = useState(false);
    const [newEspecialidad, setNewEspecialidad] = useState('');
    
    // Acceder a Firestore
    const db = getFirestore(firebaseApp);
    
    // Obtener RUC del usuario autenticado
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';
    
    // Esquema de validación
    const validationSchema = Yup.object({
        nombre: Yup.string()
            .required('El nombre de la profesión es obligatorio')
            .min(3, 'El nombre debe tener al menos 3 caracteres'),
        nivelEducativo: Yup.string()
            .required('El nivel educativo es obligatorio')
    });
    
    // Configuración del formulario
    const formik = useFormik({
        initialValues: {
            nombre: profesion?.nombre || '',
            nivelEducativo: profesion?.nivelEducativo || 'universitaria',
            descripcion: profesion?.descripcion || '',
            especialidades: profesion?.especialidades || []
        },
        validationSchema,
        onSubmit: handleSubmit
    });
    
    // Función para guardar la profesión
    async function handleSubmit(values) {
        try {
            setSubmitting(true);
            
            // Preparar datos para guardar
            const profesionData = {
                nombre: values.nombre.trim(),
                nivelEducativo: values.nivelEducativo,
                descripcion: values.descripcion.trim(),
                especialidades: values.especialidades,
                ruc,
                empresa: nombreEmpresa
            };
            
            // Si es nueva profesión
            if (!profesion) {
                profesionData.fechaCreacion = serverTimestamp();
                profesionData.usos = 0;
                
                // Crear en Firestore
                const docRef = await addDoc(collection(db, 'rs_profesiones'), profesionData);
                
                // Llamar al callback con los datos y el ID
                onSave({ id: docRef.id, ...profesionData });
            } else {
                // Para actualización, solo enviar los datos (la actualización se maneja en el componente padre)
                onSave(profesionData);
            }
        } catch (error) {
            console.error("Error guardando profesión:", error);
            alert("Error al guardar la profesión. Por favor, inténtelo nuevamente.");
        } finally {
            setSubmitting(false);
        }
    }
    
    // Función para agregar especialidad
    const handleAddEspecialidad = () => {
        if (newEspecialidad.trim() && !formik.values.especialidades.includes(newEspecialidad.trim())) {
            formik.setFieldValue('especialidades', [...formik.values.especialidades, newEspecialidad.trim()]);
            setNewEspecialidad('');
        }
    };
    
    // Función para eliminar especialidad
    const handleRemoveEspecialidad = (especialidad) => {
        const especialidades = formik.values.especialidades.filter(e => e !== especialidad);
        formik.setFieldValue('especialidades', especialidades);
    };
    
    // Función para formatear niveles educativos
    const formatNivelEducativo = (nivel) => {
        const niveles = {
            'secundaria': 'Secundaria Completa',
            'tecnica': 'Técnica',
            'universitaria': 'Universitaria',
            'postgrado': 'Postgrado/Maestría',
            'doctorado': 'Doctorado',
            'otro': 'Otro'
        };
        return niveles[nivel] || nivel;
    };
    
    return (
        <Form onSubmit={formik.handleSubmit}>
            <Row className="mb-3">
                <Col>
                    <Form.Group controlId="nombre">
                        <Form.Label>Nombre de la Profesión *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ej: Ingeniería Industrial"
                            {...formik.getFieldProps('nombre')}
                            isInvalid={formik.touched.nombre && !!formik.errors.nombre}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.nombre}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col>
                    <Form.Group controlId="nivelEducativo">
                        <Form.Label>Nivel Educativo *</Form.Label>
                        <Form.Select
                            {...formik.getFieldProps('nivelEducativo')}
                            isInvalid={formik.touched.nivelEducativo && !!formik.errors.nivelEducativo}
                        >
                            <option value="secundaria">Secundaria Completa</option>
                            <option value="tecnica">Técnica</option>
                            <option value="universitaria">Universitaria</option>
                            <option value="postgrado">Postgrado/Maestría</option>
                            <option value="doctorado">Doctorado</option>
                            <option value="otro">Otro</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.nivelEducativo}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col>
                    <Form.Group controlId="descripcion">
                        <Form.Label>Descripción</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Descripción breve sobre esta profesión o carrera..."
                            {...formik.getFieldProps('descripcion')}
                        />
                        <Form.Text className="text-muted">
                            Opcional: Agregar una descripción breve puede ayudar a clarificar el alcance de esta profesión.
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Especialidades</Form.Label>
                <InputGroup>
                    <Form.Control
                        type="text"
                        placeholder="Ej: Gestión de Calidad"
                        value={newEspecialidad}
                        onChange={(e) => setNewEspecialidad(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddEspecialidad();
                            }
                        }}
                    />
                    <Button 
                        variant="outline-primary" 
                        onClick={handleAddEspecialidad}
                    >
                        <FaPlus />
                    </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                    Agregue especialidades o áreas de énfasis relacionadas con esta profesión.
                </Form.Text>
            </Form.Group>

            {formik.values.especialidades.length > 0 && (
                <div className="mb-4">
                    <p className="mb-2">Especialidades agregadas:</p>
                    <div className="d-flex flex-wrap gap-2">
                        {formik.values.especialidades.map((especialidad, index) => (
                            <Badge 
                                key={index}
                                bg="light"
                                text="dark"
                                className="d-flex align-items-center p-2"
                            >
                                {especialidad}
                                <Button
                                    variant="link"
                                    className="p-0 ms-2 text-danger"
                                    onClick={() => handleRemoveEspecialidad(especialidad)}
                                >
                                    <FaTimes size={12} />
                                </Button>
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
                <Button 
                    variant="outline-secondary" 
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    type="submit"
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Guardando...
                        </>
                    ) : (
                        profesion ? 'Actualizar Profesión' : 'Crear Profesión'
                    )}
                </Button>
            </div>
        </Form>
    );
};

export default ProfesionForm;